// src/pages/UpgradeSuccess.js
//
// Where Stripe sends someone after they pay.
//
// Two jobs, and it used to only do the first:
//
// 1. WAIT, DON'T ASSUME. The webhook that grants access fires independently
//    of this redirect and can land a few seconds later. A page that said
//    "you're in" and linked straight to a dashboard would bounce a fast
//    clicker off PrivateRoute seconds after they paid.
//
// 2. RECONCILE IF THE WEBHOOK DOESN'T COME. The webhook not arriving is not
//    hypothetical — it doesn't run if it was never registered in the Stripe
//    Dashboard, if the event type isn't ticked, if the signing secret
//    rotated, or if the purchase came through a Payment Link that carried no
//    metadata for it to work with. Previously this page just span forever and
//    eventually suggested emailing support.
//
//    Now, after a few seconds of nothing, it calls syncMySubscription, which
//    asks Stripe directly whether this person has a live subscription and
//    performs the same writes the webhook would have. Most of the time the
//    webhook wins the race and the sync is never needed; when it is needed it
//    turns a support email into a four-second delay.
//
// There is also always a way back to the site now, in every state of this
// page, which is the thing Stripe's own confirmation page cannot offer.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/firebaseConfig";
import useAccessLevel from "../hooks/useAccessLevel";
import { dashboardPathFor } from "../utils/accountRole";

// How long to let the webhook win before asking Stripe ourselves.
const SYNC_AFTER_MS = 5000;
// How long before admitting something is wrong.
const SLOW_AFTER_MS = 20000;

export default function UpgradeSuccess() {
  const navigate = useNavigate();
  const { loading, hasPaidSubscription, subscriptionStatus, profile, isAdmin } =
    useAccessLevel();

  const [slow, setSlow] = useState(false);
  const [syncState, setSyncState] = useState("idle"); // idle | working | done | failed
  const [syncNote, setSyncNote] = useState("");
  const syncAttempted = useRef(false);

  const ready = hasPaidSubscription;

  const runSync = useCallback(async () => {
    setSyncState("working");
    setSyncNote("");
    try {
      const syncMySubscription = httpsCallable(functions, "syncMySubscription");
      const { data } = await syncMySubscription();

      if (data?.found) {
        setSyncState("done");
        // The subscription is recorded but no role could be composed, which
        // means the account is missing a state or a grade. Say so plainly
        // instead of leaving them on a page that looks stuck.
        if (data.roleGranted === false) {
          setSyncNote(
            "Your subscription is active. We still need your state and grade to finish opening your grade-level content."
          );
        }
        return;
      }

      setSyncState("failed");
      setSyncNote(
        data?.reason === "no_live_subscription"
          ? "Stripe doesn't show an active subscription on this account yet."
          : "We couldn't find a Stripe customer matching this account."
      );
    } catch (err) {
      console.error("syncMySubscription failed:", err?.code, err?.message);
      setSyncState("failed");
      setSyncNote("We couldn't reach the billing service just now.");
    }
  }, []);

  // Give the webhook a head start, then reconcile.
  useEffect(() => {
    if (loading || ready || syncAttempted.current) return undefined;

    const t = setTimeout(() => {
      syncAttempted.current = true;
      runSync();
    }, SYNC_AFTER_MS);

    return () => clearTimeout(t);
  }, [loading, ready, runSync]);

  useEffect(() => {
    const t = setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => clearTimeout(t);
  }, []);

  const goToDashboard = () =>
    navigate(dashboardPathFor(profile || {}, { isAdmin }));

  return (
    <div style={s.page}>
      <div style={s.card}>
        {ready ? (
          <>
            <div style={s.check}>✓</div>
            <h1 style={s.title}>You're all set</h1>
            <p style={s.body}>
              {subscriptionStatus === "trialing"
                ? "Your subscription is active and your free trial has started. Everything is unlocked from now."
                : "Your subscription is active and your account has been unlocked."}
            </p>
            {syncNote && <p style={s.note}>{syncNote}</p>}
            <button type="button" style={s.primary} onClick={goToDashboard}>
              Go to my dashboard
            </button>
          </>
        ) : (
          <>
            <h1 style={s.title}>Payment received</h1>
            <p style={s.body}>
              {syncState === "working"
                ? "Confirming your subscription with Stripe…"
                : loading
                ? "Checking your account…"
                : "We're finishing setting up your access. This usually takes a few seconds."}
            </p>

            {syncState !== "failed" && <div style={s.spinner} />}

            {syncState === "failed" && (
              <>
                <p style={s.note}>{syncNote}</p>
                <button type="button" style={s.primary} onClick={() => runSync()}>
                  Try again
                </button>
              </>
            )}

            {slow && syncState !== "failed" && (
              <p style={s.slow}>
                Taking longer than usual. Your payment went through — if this
                doesn't clear in a minute, email{" "}
                <a href="mailto:support@smartboardlearning.com">
                  support@smartboardlearning.com
                </a>{" "}
                and we'll sort it out.
              </p>
            )}
          </>
        )}

        {/* A way back, in every state. The whole reason this page exists
            rather than letting Stripe show its own confirmation screen is
            that Stripe's has no door back to the site. */}
        <div style={s.exits}>
          <Link to="/dashboard" style={s.quietLink}>
            Back to Smart Board Learning
          </Link>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    background: "#f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  card: {
    background: "#fff",
    maxWidth: 460,
    width: "100%",
    borderRadius: 12,
    padding: 36,
    boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
    textAlign: "center",
  },
  check: {
    width: 56,
    height: 56,
    lineHeight: "56px",
    borderRadius: "50%",
    margin: "0 auto 16px",
    background: "linear-gradient(90deg, #e100ff, #7f00ff)",
    color: "#fff",
    fontSize: 30,
    fontWeight: 700,
  },
  title: { margin: 0, fontSize: "clamp(1.5rem, 4vw, 1.9rem)", color: "#111" },
  body: { color: "#444", fontSize: 15, lineHeight: 1.6, marginTop: 12 },
  note: {
    color: "#92400e",
    background: "#fef3c7",
    border: "1px solid #fcd34d",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 14,
    lineHeight: 1.5,
    marginTop: 16,
    textAlign: "left",
  },
  primary: {
    width: "100%",
    marginTop: 22,
    padding: "12px 18px",
    borderRadius: 8,
    border: "none",
    background: "linear-gradient(90deg, #e100ff, #7f00ff)",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },
  spinner: {
    width: 28,
    height: 28,
    margin: "22px auto 0",
    border: "3px solid #e5e7eb",
    borderTopColor: "#7f00ff",
    borderRadius: "50%",
    animation: "sbl-spin 0.9s linear infinite",
  },
  slow: { fontSize: 13, color: "#6b7280", marginTop: 18, lineHeight: 1.6 },
  exits: {
    marginTop: 24,
    paddingTop: 16,
    borderTop: "1px solid #e5e7eb",
  },
  quietLink: {
    color: "#7f00ff",
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "none",
  },
};

// The spinner needs a keyframe, and this page has no stylesheet of its own.
if (typeof document !== "undefined" && !document.getElementById("sbl-spin-kf")) {
  const style = document.createElement("style");
  style.id = "sbl-spin-kf";
  style.textContent = "@keyframes sbl-spin{to{transform:rotate(360deg)}}";
  document.head.appendChild(style);
}
